import { t, useLanguage } from '../../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, StyleSheet, View, Text, ScrollView } from 'react-native';
import { Button, Card, RadioButton, useTheme } from 'react-native-paper';
import { collection, doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { firestoreCache, CACHE_KEYS } from '../../utils/firestoreCache';
import { Course } from '../../types';
import { GradeValue } from '../../types/review';
import { getCourseId, getGradeSubmissionKey, getOfferingKey, normalizeSemester, offeringDocument } from '../../utils/courseIdentity';
import { getCurrentSemester, isInGradePeriod } from '../../constants/reviewPeriods';
import { useGradeGateContext } from '../../contexts/GradeGateContext';

type Props = {
  navigation: any;
  route: {
    params: {
      course: Course;
    };
  };
};

const getAcademicYearValue = (course: Course): string => {
  if (course.academicYear) return String(course.academicYear).replace(/[^0-9]/g, '');
  return String(new Date().getFullYear());
};

const GradeSubmitScreen = ({ navigation, route }: Props) => {
  useLanguage();
  const theme = useTheme();
  const { course } = route.params;
  const { notifyGradeSubmitted } = useGradeGateContext();

  const [grade, setGrade] = useState<GradeValue | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const academicYear = useMemo(() => getAcademicYearValue(course), [course]);
  const semester = useMemo(() => normalizeSemester(getCurrentSemester().semester), []);

  useEffect(() => {
    if (!auth.currentUser) {
      Alert.alert(t("ログインが必要です"), t("成績報告にはログインが必要です"));
      navigation.goBack();
      return;
    }

    const validateReviewExists = async () => {
      try {
        setChecking(true);

        if (!isInGradePeriod()) {
          Alert.alert(t("受付前"), t("成績入力は8月15日以降に開始されます"));
          navigation.goBack();
          return;
        }

        const uid = auth.currentUser!.uid;
        const reviewDoc = await getDoc(doc(offeringDocument(db, course), 'reviews', uid));
        if (!reviewDoc.exists()) {
          Alert.alert(t("レビュー未投稿"), t("先に講義レビューを投稿してください"));
          navigation.goBack();
          return;
        }

        const reviewData = reviewDoc.data() as { academicYear?: string; semester?: string };
        if (reviewData.academicYear !== academicYear || reviewData.semester !== semester) {
          Alert.alert(t("対象外"), t("当該学期のレビュー投稿後に成績入力が可能です"));
          navigation.goBack();
          return;
        }

        const submissionMarker = await getDoc(
          doc(db, 'users', uid, 'gradeSubmissions', getGradeSubmissionKey(course, semester))
        );
        if (submissionMarker.exists()) {
          setAlreadySubmitted(true);
        }
      } catch (e) {
        console.error('[GradeSubmitScreen] validateReviewExists failed:', e);
        Alert.alert(t("エラー"), t("レビュー状態の確認に失敗しました"));
        navigation.goBack();
      } finally {
        setChecking(false);
      }
    };

    validateReviewExists();
  }, [academicYear, course.id, navigation, semester]);

  const handleSubmitGrade = async () => {
    if (!auth.currentUser) return;
    if (!grade) {
      Alert.alert(t("未選択"), t("成績を選択してください"));
      return;
    }
    if (alreadySubmitted) {
      Alert.alert(t("投稿済み"), t("この授業の成績はすでに入力済みです"));
      return;
    }

    try {
      setSubmitting(true);

      const uid = auth.currentUser.uid;
      const courseId = getCourseId(course);
      const submissionKey = getGradeSubmissionKey(course, semester);
      const gradeRef = doc(collection(offeringDocument(db, course), 'grades'));
      const markerRef = doc(db, 'users', uid, 'gradeSubmissions', submissionKey);
      const batch = writeBatch(db);

      batch.set(gradeRef, {
        grade,
        courseId,
        academicYear,
        semester,
        submissionKey,
        createdAt: serverTimestamp(),
      });
      batch.set(markerRef, {
        gradeId: gradeRef.id,
        courseId,
        academicYear,
        semester,
        createdAt: serverTimestamp(),
      });
      await batch.commit();

      // キャッシュクリア
      firestoreCache.invalidate(CACHE_KEYS.courseReviews(getOfferingKey(course), academicYear));
      firestoreCache.invalidate(`${CACHE_KEYS.courseReviews(getOfferingKey(course), academicYear)}:details`);

      // alreadySubmittedチェックにより既存投稿では到達しないため、常に新規報告として即時反映する
      notifyGradeSubmitted();

      Alert.alert(t("登録完了"), t("成績を報告しました"));
      navigation.goBack();
    } catch (e) {
      console.error('[GradeSubmitScreen] submit failed:', e);
      Alert.alert(t("エラー"), t("成績報告に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}> 
        <Text style={{ color: theme.colors.onSurfaceVariant }}>{t("確認中...")}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]} contentContainerStyle={styles.scrollContent}>
      <Card style={styles.card}>
        <Card.Content>
          <Text style={[styles.title, { color: theme.colors.onSurface }]}>{t("成績報告")}</Text>
          <Text style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}>{course.name}</Text>
          <Text style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}>{t('{0}年度 / {1}{2}', [academicYear, t(semester), ''])}</Text>
          {alreadySubmitted ? <Text style={[styles.warnText, { color: theme.colors.error }]}>{t("この授業は成績入力済みです")}</Text> : null}

          <View style={[styles.noticeContainer, { backgroundColor: theme.colors.primaryContainer, borderColor: theme.colors.primary }]}>
            <Text style={[styles.noticeText, { color: theme.colors.onPrimaryContainer }]}> 
              {t("※成績入力は完全に匿名で行われます。登録された成績は統計データとしてのみ処理され、教員や他のユーザーに個人が特定されることは一切ありません。")}
            </Text>
          </View>

          <Text style={[styles.label, { color: theme.colors.onSurface }]}>{t("取得成績")}</Text>
          <RadioButton.Group value={grade} onValueChange={(v) => setGrade(v as GradeValue)}>
            {['S', 'A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'F'].map((g) => (
              <View key={g} style={styles.radioLine}>
                <RadioButton value={g} />
                <Text style={{ color: theme.colors.onSurface }}>{g}</Text>
              </View>
            ))}
          </RadioButton.Group>

          <Button mode="contained" onPress={handleSubmitGrade} loading={submitting} disabled={submitting || alreadySubmitted || !grade} style={styles.submitButton}>
            {t("成績を登録")}
          </Button>
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    marginTop: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  subtitle: {
    opacity: 0.7,
    marginBottom: 4,
  },
  label: {
    marginTop: 14,
    marginBottom: 4,
    fontWeight: '700',
  },
  warnText: {
    marginTop: 8,
    fontWeight: '600',
  },
  radioLine: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scrollContent: {
    paddingBottom: 32,
  },
  noticeContainer: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    marginBottom: 8,
  },
  noticeText: {
    fontSize: 12,
    lineHeight: 18,
    fontWeight: '500',
  },
  submitButton: {
    marginTop: 16,
  },
});

export default GradeSubmitScreen;
